import React, { Suspense, lazy } from 'react';
import { useNavigate } from 'react-router-dom';
import ContactLogo from '../components/shared/ContactLogo';
import { getLegalContent } from '../constants/legalContent';

const RuixenGradientFooter = lazy(() =>
  import('@/components/ui/ruixen-gradient-footer').then((m) => ({ default: m.RuixenGradientFooter }))
);

// Shared with DocsPage.js on purpose: picking a language on either page
// carries over to the other, since both are the same "site content
// language" preference from the reader's point of view.
const LANG_STORAGE_KEY = 'contact_docs_lang';

const LANG_OPTIONS = [
  { key: 'es', label: 'ES' },
  { key: 'en', label: 'EN' },
  { key: 'pt', label: 'PT' },
];

const UI = {
  es: { backHome: 'Volver al inicio', onThisPage: 'En esta página', seeAlso: 'Ver también', changeLanguage: 'Cambiar idioma' },
  en: { backHome: 'Back to home', onThisPage: 'On this page', seeAlso: 'See also', changeLanguage: 'Change language' },
  pt: { backHome: 'Voltar ao início', onThisPage: 'Nesta página', seeAlso: 'Ver também', changeLanguage: 'Mudar idioma' },
};

const FOOTER_UI = {
  es: {
    product: 'Producto', features: 'Características', plans: 'Planes', schedule: 'Agendar reunión', docs: 'Documentación',
    company: 'Empresa', about: 'Nosotros',
    legal: 'Legal', terms: 'Términos y Condiciones', privacy: 'Política de Privacidad', dataProcessing: 'Tratamiento de Datos', hours: 'Lun–Vie 8:00–20:00 hrs',
    security: 'Seguridad', securityText: 'Tus datos viajan cifrados con TLS y nunca los compartimos con terceros. Tú decides qué información mostrar y cuándo actualizarla.',
    copyright: '© 2026 CONTACT Network SPA · Chile', tagline: 'Conecta. Comparte. Crece.',
  },
  en: {
    product: 'Product', features: 'Features', plans: 'Pricing', schedule: 'Book a meeting', docs: 'Documentation',
    company: 'Company', about: 'About us',
    legal: 'Legal', terms: 'Terms and Conditions', privacy: 'Privacy Policy', dataProcessing: 'Data Processing', hours: 'Mon–Fri 8:00 AM–8:00 PM',
    security: 'Security', securityText: 'Your data travels encrypted with TLS and we never share it with third parties. You decide what information to show and when to update it.',
    copyright: '© 2026 CONTACT Network SPA · Chile', tagline: 'Connect. Share. Grow.',
  },
  pt: {
    product: 'Produto', features: 'Funcionalidades', plans: 'Planos', schedule: 'Agendar reunião', docs: 'Documentação',
    company: 'Empresa', about: 'Sobre nós',
    legal: 'Legal', terms: 'Termos e Condições', privacy: 'Política de Privacidade', dataProcessing: 'Tratamento de Dados', hours: 'Seg–Sex 8h–20h',
    security: 'Segurança', securityText: 'Seus dados trafegam criptografados com TLS e nunca são compartilhados com terceiros. Você decide quais informações mostrar e quando atualizá-las.',
    copyright: '© 2026 CONTACT Network SPA · Chile', tagline: 'Conecte. Compartilhe. Cresça.',
  },
};

const DOC_ROUTES = {
  terms: '/terminos',
  privacy: '/privacidad',
  dataProcessing: '/tratamiento-de-datos',
};

const slugify = (title) =>
  title
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

const IconArrowLeft = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 12H5M12 5l-7 7 7 7"/>
  </svg>
);
const IconGlobe = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 010 20M12 2a15.3 15.3 0 000 20"/>
  </svg>
);
const IconInfo = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><path d="M12 16v-5M12 8h.01"/>
  </svg>
);

const LegalPage = ({ docKey }) => {
  const navigate = useNavigate();
  const [showSecurity, setShowSecurity] = React.useState(false);
  const [langMenuOpen, setLangMenuOpen] = React.useState(false);
  const [activeSlug, setActiveSlug] = React.useState(null);
  const [lang, setLang] = React.useState(() => {
    try {
      const stored = localStorage.getItem(LANG_STORAGE_KEY);
      if (stored === 'en' || stored === 'pt' || stored === 'es') return stored;
    } catch {}
    return 'es';
  });
  const langMenuRef = React.useRef(null);
  const sectionRefs = React.useRef({});

  const legal = getLegalContent(lang);
  const data = legal[docKey];
  const t = UI[lang] || UI.es;
  const footerT = FOOTER_UI[lang] || FOOTER_UI.es;
  const slugs = React.useMemo(() => data.sections.map(([title]) => slugify(title)), [data]);

  React.useEffect(() => {
    try { localStorage.setItem(LANG_STORAGE_KEY, lang); } catch {}
  }, [lang]);

  React.useEffect(() => {
    document.title = `${data.title} · Contact`;
    window.scrollTo(0, 0);
    setActiveSlug(slugs[0] || null);
  }, [docKey]); // eslint-disable-line

  // Smooth-scroll anchor jumps while this page is mounted, without forcing
  // the behaviour on every other page on the site.
  React.useEffect(() => {
    const prev = document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = 'smooth';
    return () => { document.documentElement.style.scrollBehavior = prev; };
  }, []);

  React.useEffect(() => {
    const onClick = (e) => {
      if (langMenuRef.current && !langMenuRef.current.contains(e.target)) setLangMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  // Scroll-spy: highlight whichever section is currently under the top of
  // the viewport, same pattern used in /docs.
  React.useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      const visible = entries.filter(en => en.isIntersecting).map(en => en.target.dataset.slug);
      if (visible.length) setActiveSlug(visible[0]);
    }, { rootMargin: '-96px 0px -70% 0px', threshold: 0 });
    Object.values(sectionRefs.current).forEach(el => el && observer.observe(el));
    return () => observer.disconnect();
  }, [lang, docKey]);

  const jumpTo = (slug) => (e) => {
    e.preventDefault();
    const el = sectionRefs.current[slug];
    if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); setActiveSlug(slug); }
  };

  const otherDocs = Object.keys(legal).filter((k) => k !== docKey);

  return (
    <div className="lp-root">
      <header className="lp-topbar">
        <div className="lp-topbar-inner">
          <ContactLogo clickable/>
          <div className="lp-topbar-right">
            <div className="lp-lang-wrap" ref={langMenuRef}>
              <button className="lp-icon-btn" onClick={() => setLangMenuOpen(v => !v)} aria-label={t.changeLanguage} title={t.changeLanguage}>
                <IconGlobe/><span className="lp-lang-code">{lang.toUpperCase()}</span>
              </button>
              {langMenuOpen && (
                <div className="lp-lang-menu">
                  {LANG_OPTIONS.map(opt => (
                    <button
                      key={opt.key}
                      className={`lp-lang-option ${lang === opt.key ? 'lp-lang-option--active' : ''}`}
                      onClick={() => { setLang(opt.key); setLangMenuOpen(false); }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button className="lp-btn-back" onClick={() => navigate('/')}>
              <IconArrowLeft/> <span>{t.backHome}</span>
            </button>
          </div>
        </div>
      </header>

      <div className="lp-shell" key={lang}>
        <main className="lp-content">
          <h1 className="lp-title">{data.title}</h1>
          <p className="lp-heading">{data.heading}</p>

          {data.translationNotice && (
            <div className="lp-notice">
              <span className="lp-notice-ico"><IconInfo/></span>
              <span>{data.translationNotice}</span>
            </div>
          )}

          {data.sections.map(([title, body], i) => (
            <section
              key={i}
              id={slugs[i]}
              ref={el => { sectionRefs.current[slugs[i]] = el; }}
              data-slug={slugs[i]}
              className="lp-section"
            >
              {i > 0 && <div className="lp-section-divider"/>}
              <h2 className="lp-section-title">{title}</h2>
              <div className="lp-section-body">
                {body.split('\n\n').map((p, j) => <p key={j} className="lp-p">{p}</p>)}
              </div>
            </section>
          ))}

          <div className="lp-see-also">
            <span className="lp-see-also-label">{t.seeAlso}</span>
            {otherDocs.map((k) => (
              <button key={k} className="lp-see-also-link" onClick={() => navigate(DOC_ROUTES[k])}>
                {legal[k].title}
              </button>
            ))}
          </div>
        </main>

        <aside className="lp-toc">
          <div className="lp-toc-label">{t.onThisPage}</div>
          {data.sections.map(([title], i) => (
            <a
              key={i}
              href={`#${slugs[i]}`}
              className={`lp-toc-link ${activeSlug === slugs[i] ? 'lp-toc-link--active' : ''}`}
              onClick={jumpTo(slugs[i])}
            >
              {title}
            </a>
          ))}
        </aside>
      </div>

      <Suspense fallback={null}>
        <RuixenGradientFooter className="nx-footer lp-footer" gradientHeight={typeof window !== 'undefined' && window.innerWidth < 640 ? '10vh' : '18vh'}>
          <div className="nx-container">
            <div className="nx-footer-grid">
              <div>
                <div className="nx-footer-socials">
                  <a href="https://instagram.com/contact_cl" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="nx-footer-social">
                    <svg width="15" height="15" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                  </a>
                  <a href="https://www.linkedin.com/company/contactnetworkcl" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="nx-footer-social">
                    <svg width="15" height="15" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                  </a>
                </div>
              </div>
              <div>
                <div className="nx-footer-col-title">{footerT.product}</div>
                <div className="nx-footer-links">
                  <a href="/#caracteristicas">{footerT.features}</a>
                  <a href="/#precios">{footerT.plans}</a>
                  <button onClick={() => navigate('/docs')} className="nx-footer-link-btn">{footerT.docs}</button>
                  <button onClick={() => navigate('/agenda-demo')} className="nx-footer-link-btn">{footerT.schedule}</button>
                </div>
              </div>
              <div>
                <div className="nx-footer-col-title">{footerT.company}</div>
                <div className="nx-footer-links">
                  <a href="/sobre-nosotros">{footerT.about}</a>
                  <a href="mailto:soporte@contactnetwork.cl">soporte@contactnetwork.cl</a>
                  <a href="tel:+56982036712">+56 9 8203 6712</a>
                </div>
              </div>
              <div>
                <div className="nx-footer-col-title">{footerT.legal}</div>
                <div className="nx-footer-links">
                  <button onClick={() => navigate('/terminos')} className="nx-footer-link-btn">{footerT.terms}</button>
                  <button onClick={() => navigate('/privacidad')} className="nx-footer-link-btn">{footerT.privacy}</button>
                  <button onClick={() => navigate('/tratamiento-de-datos')} className="nx-footer-link-btn">{footerT.dataProcessing}</button>
                  <span className="nx-footer-detail">{footerT.hours}</span>
                </div>
              </div>
              <div>
                <button className="nx-footer-sec-btn" onClick={() => setShowSecurity(s => !s)}>
                  <span className="nx-footer-col-title" style={{margin:0}}>{footerT.security}</span>
                  <svg className={'nx-footer-sec-chevron' + (showSecurity ? ' nx-footer-sec-open' : '')} width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M6 9l6 6 6-6"/></svg>
                </button>
                {showSecurity && (
                  <p className="nx-footer-sec-text">{footerT.securityText}</p>
                )}
              </div>
            </div>
            <div className="nx-footer-bottom">
              <span style={{fontFamily:"'Inter',system-ui,sans-serif",fontWeight:300,letterSpacing:'-.025em'}}>{footerT.copyright}</span>
              <span>{footerT.tagline}</span>
            </div>
          </div>
        </RuixenGradientFooter>
      </Suspense>

      <style>{CSS}</style>
    </div>
  );
};

/* ─── CSS ──────────────────────────────── */
const CSS = `
:root{
  --lp-bg:#0B0B0D;
  --lp-surface:#15161A;
  --lp-text:#F2F2F3;
  --lp-muted:#9A9AA2;
  --lp-faint:#6B6B73;
  --lp-border:rgba(255,255,255,.09);
  --lp-border-dk:rgba(255,255,255,.16);
  --lp-blue:#4DA3FF;
  --lp-shadow:rgba(0,0,0,.5);
  --lp-footer-icon-border:rgba(255,255,255,.12);
  --lp-footer-icon-color:rgba(255,255,255,.6);
  --lp-footer-chevron:rgba(255,255,255,.45);
  --lp-topbar-bg:rgba(11,11,13,.82);
}

.lp-root{font-family:'Inter',system-ui,sans-serif;background:var(--lp-bg);color:var(--lp-text);min-height:100vh;line-height:1.6}

@keyframes lpFadeIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}

.lp-topbar{position:sticky;top:0;z-index:50;background:var(--lp-topbar-bg);backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);border-bottom:1px solid var(--lp-border);transition:background .2s ease}
.lp-topbar-inner{max-width:1040px;margin:0 auto;padding:0 20px;height:56px;display:flex;align-items:center;justify-content:space-between;gap:12px}
.lp-topbar-right{display:flex;align-items:center;gap:10px;flex-shrink:0}
.lp-btn-back{display:inline-flex;align-items:center;gap:6px;background:none;border:none;color:var(--lp-muted);font-family:inherit;font-size:13px;font-weight:500;cursor:pointer;padding:0;transition:color .15s;white-space:nowrap}
.lp-btn-back:hover{color:var(--lp-text)}
.lp-icon-btn{flex-shrink:0;display:flex;align-items:center;gap:4px;background:none;border:1px solid var(--lp-border-dk);color:var(--lp-muted);border-radius:8px;height:30px;padding:0 9px;cursor:pointer;transition:color .15s,border-color .15s,background .15s;font-family:inherit;font-size:12px;font-weight:600}
.lp-icon-btn:hover{color:var(--lp-text);border-color:var(--lp-faint);background:var(--lp-surface)}
.lp-lang-wrap{position:relative;flex-shrink:0}
.lp-lang-code{letter-spacing:.02em}
.lp-lang-menu{position:absolute;top:calc(100% + 8px);right:0;background:var(--lp-surface);border:1px solid var(--lp-border-dk);border-radius:10px;box-shadow:0 12px 30px var(--lp-shadow);overflow:hidden;z-index:60;min-width:72px;animation:lpFadeIn .15s ease both}
.lp-lang-option{display:block;width:100%;text-align:left;background:none;border:none;font-family:inherit;font-size:13px;font-weight:500;color:var(--lp-text);cursor:pointer;padding:8px 14px;transition:background .12s}
.lp-lang-option:hover{background:var(--lp-bg)}
.lp-lang-option--active{color:var(--lp-blue);font-weight:700}

.lp-shell{display:grid;grid-template-columns:minmax(0,1fr) 220px;gap:40px;max-width:1040px;margin:0 auto;padding:0 24px;align-items:start}
.lp-content{min-width:0;max-width:700px;padding:48px 0 100px;animation:lpFadeIn .35s cubic-bezier(.16,1,.3,1) both}

.lp-title{font-size:clamp(28px,3.4vw,36px);font-weight:300;letter-spacing:-.02em;margin-bottom:10px;text-wrap:balance}
.lp-heading{font-size:12.5px;font-weight:600;color:var(--lp-faint);text-transform:uppercase;letter-spacing:.07em;margin-bottom:24px}

.lp-notice{display:flex;gap:10px;align-items:flex-start;border-radius:10px;padding:13px 15px;font-size:13px;line-height:1.6;margin-bottom:32px;background:rgba(0,113,227,.09);color:var(--lp-text)}
.lp-notice-ico{flex-shrink:0;margin-top:1px;color:var(--lp-blue)}

.lp-section{scroll-margin-top:72px}
.lp-section-divider{border-top:1px solid var(--lp-border);margin:34px 0}
.lp-section-title{font-size:19px;font-weight:600;letter-spacing:-.01em;margin-bottom:12px}
.lp-section-body{font-size:15px;color:var(--lp-text)}
.lp-p{margin-bottom:15px;line-height:1.75;color:var(--lp-muted);overflow-wrap:break-word}

.lp-see-also{margin-top:48px;padding-top:26px;border-top:1px solid var(--lp-border);display:flex;flex-wrap:wrap;align-items:center;gap:10px 16px}
.lp-see-also-label{font-size:11px;font-weight:700;color:var(--lp-faint);text-transform:uppercase;letter-spacing:.06em}
.lp-see-also-link{background:none;border:none;font-family:inherit;font-size:13px;font-weight:600;color:var(--lp-blue);cursor:pointer;padding:0;transition:opacity .15s}
.lp-see-also-link:hover{opacity:.7;text-decoration:underline}

.lp-toc{position:sticky;top:76px;height:fit-content;max-height:calc(100vh - 96px);overflow-y:auto;display:flex;flex-direction:column;gap:1px;padding:48px 0 20px 18px;border-left:1px solid var(--lp-border);animation:lpFadeIn .35s cubic-bezier(.16,1,.3,1) both}
.lp-toc-label{font-size:11px;font-weight:700;color:var(--lp-faint);text-transform:uppercase;letter-spacing:.06em;margin-bottom:10px}
.lp-toc-link{text-align:left;text-decoration:none;display:block;border-left:2px solid transparent;margin-left:-19px;padding:5px 0 5px 17px;font-family:inherit;font-size:12.5px;color:var(--lp-muted);cursor:pointer;line-height:1.6;transition:color .15s,border-color .15s}
.lp-toc-link:hover{color:var(--lp-text)}
.lp-toc-link--active{color:var(--lp-blue);border-color:var(--lp-blue);font-weight:600}

@media(max-width:860px){
  .lp-shell{grid-template-columns:minmax(0,1fr);padding:0 20px}
  .lp-toc{display:none}
  .lp-content{padding:32px 0 60px}
}
@media(max-width:480px){
  .lp-topbar-inner{padding:0 16px;gap:8px}
  .lp-btn-back span{display:none}
  .lp-lang-code{display:none}
  .lp-icon-btn{padding:0 7px}
  .lp-notice{font-size:12.5px}
}

/* ── FOOTER (shared Homepage/Docs footer, reused here) ── */
.nx-container{max-width:1280px;margin:0 auto;padding:0 40px}
.lp-footer{background:var(--lp-bg);padding:48px 0 16px;margin-top:40px}
.nx-footer-grid{display:grid;grid-template-columns:2fr 1fr 1fr 1fr 1fr;gap:14px;padding-bottom:14px;border-bottom:1px solid var(--lp-border)}
.nx-footer-sec-btn{display:flex;align-items:center;gap:6px;background:none;border:none;cursor:pointer;padding:0;margin-bottom:6px;font-family:inherit}
.nx-footer-sec-chevron{color:var(--lp-footer-chevron);transition:transform .2s ease;flex-shrink:0;margin-top:1px}
.nx-footer-sec-open{transform:rotate(180deg)}
.nx-footer-sec-text{font-size:11px;color:var(--lp-muted);line-height:1.4;margin:0}
.nx-footer-socials{display:flex;gap:8px;margin-top:6px}
.nx-footer-social{width:22px;height:22px;border-radius:6px;background:var(--lp-border);border:1px solid var(--lp-footer-icon-border);color:var(--lp-footer-icon-color);display:flex;align-items:center;justify-content:center;text-decoration:none;transition:all .15s}
.nx-footer-social:hover{border-color:var(--lp-text);color:var(--lp-text)}
.nx-footer-col-title{font-size:10px;font-weight:400;color:var(--lp-text);text-transform:uppercase;letter-spacing:.07em;margin-bottom:6px}
.nx-footer-links{display:flex;flex-direction:column;gap:5px}
.nx-footer-links a{font-size:12px;color:var(--lp-muted);text-decoration:none;transition:color .15s}
.nx-footer-links a:hover{color:var(--lp-text)}
.nx-footer-link-btn{background:none;border:none;cursor:pointer;font-family:inherit;font-size:12px;color:var(--lp-muted);text-align:left;padding:0;transition:color .15s}
.nx-footer-link-btn:hover{color:var(--lp-text)}
.nx-footer-detail{font-size:11px;color:var(--lp-faint)}
.nx-footer-bottom{padding:6px 0;display:flex;align-items:center;justify-content:space-between;gap:14px}
.nx-footer-bottom>span{font-size:11px;color:var(--lp-faint)}
@media(max-width:1024px){
  .nx-container{padding:0 28px}
  .nx-footer-grid{grid-template-columns:1fr 1fr;gap:32px}
}
@media(max-width:640px){
  .nx-container{padding:0 18px}
  .lp-footer{padding:20px 0 10px;margin-top:0}
  .nx-footer-grid{grid-template-columns:1fr;gap:18px;padding-bottom:16px}
  .nx-footer-bottom{flex-wrap:wrap;justify-content:center;gap:6px 16px;text-align:center;padding:6px 0}
  .nx-footer-bottom>span{font-size:11px}
}

@media(prefers-reduced-motion:reduce){
  .lp-root *{animation-duration:.01ms !important;transition-duration:.01ms !important;scroll-behavior:auto !important}
}
`;

export default LegalPage;
